import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { CalendarDate } from "@/src/types/calendarDay";
import { useBackToCloseModal } from "../../../../hooks/useBackToCloseModal";

interface CalendarModalProps {
  setShowBottomSheet: (show: boolean) => void;
  showBottomSheet: boolean;
  navigateMonth: (path: string) => void;
  currentMonth: number;
  currentYear: number;
  canNavigateToMonth: (month: number, year: number) => boolean;
  monthNames: string;
  weekDays: string[];
  calendarDays: CalendarDate[];
  handleDateSelect: (day: CalendarDate) => void;
  isDateSelected: (day: CalendarDate) => boolean;
}
//MOBILE
const CalendarModal = ({
  setShowBottomSheet,
  showBottomSheet,
  navigateMonth,
  currentMonth,
  currentYear,
  canNavigateToMonth,
  monthNames,
  weekDays,
  calendarDays,
  handleDateSelect,
  isDateSelected,
}: CalendarModalProps) => {
  useBackToCloseModal(
    showBottomSheet,
    () => setShowBottomSheet(false),
    "calendar-modal"
  );

  return (
    <div>
      {/* Calendar Navigation */}
      <div className="color-primary flex items-center justify-between p-2 border-b border-gray-200">
        <button
          onClick={() => navigateMonth("prev")}
          className={`p-2 font-bold hover:bg-gray-100 hover:text-black rounded-full text-white
          ${
            !canNavigateToMonth(
              currentMonth === 1 ? 12 : currentMonth - 1,
              currentMonth === 1 ? currentYear - 1 : currentYear
            )
              ? "invisible"
              : ""
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-base font-medium text-white">
          {monthNames[currentMonth - 1]} - {currentYear}
        </span>
        <button
          onClick={() => navigateMonth("next")}
          className={`p-2 font-bold hover:bg-gray-100 hover:text-black text-white rounded-full
          ${
            !canNavigateToMonth(
              currentMonth === 12 ? 1 : currentMonth + 1,
              currentMonth === 12 ? currentYear + 1 : currentYear
            )
              ? "invisible"
              : ""
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-center text-sm font-bold text-gray-500 py-2"
            >
              <span>{day}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day, index) => (
            <button
              key={index}
              onClick={() => handleDateSelect(day)}
              disabled={!day.isSelectable || !day.isCurrentMonth}
              className={`h-12 text-sm font-medium transition-colors
              ${day.isToday ? "border-b-4 border-red-500" : ""}
              ${
                !day.isCurrentMonth
                  ? "text-gray-300 cursor-not-allowed"
                  : isDateSelected(day)
                  ? "bg-green-500 text-white"
                  : day.isSelectable
                  ? "text-blue-500 hover:bg-blue-100 bg-blue-50"
                  : "text-gray-400 cursor-not-allowed bg-gray-100 font-normal"
              }
            `}
            >
              <span>{day.day}</span>
            </button>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-200 rounded border-b-4 border-red-500"></div>
              <span className="text-sm text-gray-600">Ngày hiện tại</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-100 rounded"></div>
              <span className="text-sm text-gray-600">Ngày có lịch</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <span className="text-sm text-gray-600">
                Ngày không có lịch
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span className="text-sm text-gray-600">Ngày đã chọn</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarModal;
